/* eslint-disable no-alert */
import { Search } from '@mui/icons-material';
import {
  Checkbox,
  FormControlLabel,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import { Stack } from '@mui/system';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridRowId,
  GridValidRowModel,
  useGridApiRef,
} from '@mui/x-data-grid';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  PortalRole,
  useCancelGroupSyncMutation,
  useCancelMachineSyncMutation,
  useClearMachinePresetVersionMutation,
  useListMachinesQuery,
  useOffboardMachinesMutation,
  useSyncPresetsMutation,
  useSyncSummaryQuery,
} from 'src/backend';
import LoadingIconButton from 'src/components/LoadingIconButton';
import PageCard from 'src/components/PageCard';
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';
import CachedOutlinedIcon from '@mui/icons-material/CachedOutlined';
import { useParams } from 'react-router-dom';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import * as React from 'react';
import PlayCircleOutlinedIcon from '@mui/icons-material/PlayCircleOutlined';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import RoleBased from 'src/components/RoleBased';
import CleaningServicesOutlinedIcon from '@mui/icons-material/CleaningServicesOutlined';
import OnboardMachineForm from './OnboardMachineForm';
import SyncProgress from './SyncProgress';
import { SyncStatus, useMachineGroupContext } from './MachineGroupContext';
import GroupSyncSummary from './GroupSyncSummary';
import TimeElapsed from './TimeElapsed';
import MachineStatusSummary from './MachineStatusSummary';
import StatusIndicator from './StatusIndicator';

export type ClearMachinePresetVersionRequest = {
  machineIds: string[] | null;
};

export type Machine = {
  id: string;
  serialNumber: string;
  machineType: string;
  machineGroupId: string;
  createdAt: Date;
  createdBy: string;
  lastModifiedAt: Date;
  lastModifiedBy: string;
  status: string;
};

export type LastSyncProcess = {
  id: string;
  start: string;
  end: string | undefined;
  status: string;
};

export type TaskSummaryAggregate = {
  total: number;
  pending: number;
  inProgress: number;
  cancelled: number;
  finished: number;
  failed: number;
  aggregatedStatus: string;
  start: string | undefined;
  end: string | undefined;
};

export type PresetSummary = {
  count: number;
  names: string[];
};

export type PresetSummaryAggregate = {
  desiredTotal: number;
  totalUpToDate: number;
  missing: PresetSummary;
  outdated: PresetSummary;
  excess: PresetSummary;
};

export type MachineSummary = {
  lastSyncProcess: LastSyncProcess | null;
  presetSummaryAggregate: PresetSummaryAggregate;
  taskSummaryAggregate: TaskSummaryAggregate;
};

export type SyncSummary = {
  lastSyncProcess: LastSyncProcess | null;
  groupTaskSummary: TaskSummaryAggregate;
  machineSummaries: { [id: string]: MachineSummary };
};

type OffboardMachinesRequest = {
  machineGroupId: string;
  serialNumbers: string[];
};

type Params = {
  id: string;
};

declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides {
    onSearch: (searchText: string | undefined) => void;
    onFilterOnline: (event: React.ChangeEvent<HTMLInputElement>) => void;
  }
}

function CustomToolbar(props: {
  onSearch: (searchText: string | undefined) => void;
}) {
  const { onSearch } = props;
  const [searchText, setSearchText] = useState('');
  return (
    <Stack direction="row-reverse">
      <IconButton
        onClick={() => onSearch(searchText === '' ? undefined : searchText)}
      >
        <Search />
      </IconButton>
      <TextField
        name="search"
        label="Search"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        onKeyUp={(e) => {
          if (e.key === 'Enter') {
            onSearch(searchText === '' ? undefined : searchText);
          }
        }}
      />
    </Stack>
  );
}

function MachineGroupMachines() {
  const { id } = useParams<Params>();
  const { machineType, setSyncStatus } = useMachineGroupContext();
  const [refetchRequired, setRefetchRequired] = useState(false);
  const [isCancelGroupDisabled, setIsCancelGroupDisabled] = useState(false);
  const [includeOffline, setIncludeOffline] = useState(false);
  const offboardMachines = useOffboardMachinesMutation();

  useEffect(() => {
    setRefetchRequired(true);
  }, [setRefetchRequired]);

  const offboardMachinesCallback = useCallback(
    (serialNumbers: string[]) => {
      const offboardMachinesRequest: OffboardMachinesRequest = {
        machineGroupId: id ?? '',
        serialNumbers,
      };
      offboardMachines[0](offboardMachinesRequest)
        .unwrap()
        .then(() => {
          setRefetchRequired(true);
        })
        .catch();
    },
    [id, offboardMachines],
  );

  const syncSummary = useSyncSummaryQuery(id);

  const [syncSummaryResult, setSyncSummaryResult] = useState(
    syncSummary?.data?.data,
  );

  useEffect(() => {
    if (syncSummary?.data?.data) {
      setSyncSummaryResult(syncSummary.data.data);
      const status =
        syncSummary.data.data.groupTaskSummary?.aggregatedStatus ?? 'Unknown';
      setSyncStatus(SyncStatus[status as keyof typeof SyncStatus]);
    }
  }, [syncSummary, setSyncStatus]);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchStatus = useCallback(() => {
    if (
      (syncSummaryResult?.groupTaskSummary?.aggregatedStatus === 'Finished' ||
        syncSummaryResult?.groupTaskSummary?.aggregatedStatus === undefined ||
        syncSummaryResult?.groupTaskSummary?.aggregatedStatus === 'Cancelled' ||
        syncSummaryResult?.groupTaskSummary?.aggregatedStatus === 'None' ||
        syncSummaryResult?.groupTaskSummary?.aggregatedStatus === 'Failed') &&
      syncSummaryResult?.lastSyncProcess?.status !== 'Pending' &&
      syncSummaryResult?.lastSyncProcess?.status !== 'InProgress' &&
      intervalRef.current
    ) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    } else {
      syncSummary.refetch();
    }
  }, [syncSummary, syncSummaryResult]);

  useEffect(() => {
    intervalRef.current = setInterval(fetchStatus, 3000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchStatus, syncSummary]);

  const syncPresets = useSyncPresetsMutation();
  const cancelGroupSync = useCancelGroupSyncMutation();
  const cancelMachineSync = useCancelMachineSyncMutation();
  const clearMachinePresetVersions = useClearMachinePresetVersionMutation();

  const apiRef = useGridApiRef();
  const [selectedRows, setSelectedRows] = useState<
    Map<GridRowId, GridValidRowModel>
  >(new Map());

  const onRowSelectionModelChange = useCallback(() => {
    const selectedRows = apiRef.current.getSelectedRows();
    setSelectedRows(selectedRows);
  }, [apiRef]);

  const requestSync = useCallback(
    (machineIds: string[] | null) => {
      const startPresetSyncRequest = {
        machineIds,
        presetIds: null,
        includeOffline,
      };
      syncPresets[0]({ id, startPresetSyncRequest })
        .unwrap()
        .then()
        .catch()
        .finally(() => {
          setRefetchRequired(true);
          setIsCancelGroupDisabled(false);
        });
    },
    [id, syncPresets, includeOffline],
  );

  const requestGroupSyncCancellation = useCallback(() => {
    cancelGroupSync[0](id)
      .unwrap()
      .then(() => {
        setRefetchRequired(true);
        setIsCancelGroupDisabled(true);
      })
      .catch();
  }, [cancelGroupSync, id]);

  const requestMachineSyncCancellation = useCallback(
    (machineId: string) => {
      cancelMachineSync[0]({ groupId: id, machineId })
        .unwrap()
        .then(() => {
          setRefetchRequired(true);
        })
        .catch();
    },
    [cancelMachineSync, id],
  );

  const clearMachinePresetVersionsCallback = useCallback(() => {
    const machineIds = Array.from(selectedRows.keys());
    const clearMachinePresetVersionRequest = {
      machineIds: machineIds.length > 0 ? machineIds : null,
    } as ClearMachinePresetVersionRequest;
    clearMachinePresetVersions[0]({
      groupId: id,
      clearMachinePresetVersionRequest,
    })
      .unwrap()
      .then(() => {
        setRefetchRequired(true);
      })
      .catch();
  }, [clearMachinePresetVersions, id, selectedRows]);

  const columns: GridColDef<Machine>[] = [
    {
      field: 'id',
      headerName: 'ID',
    },
    {
      field: 'serialNumber',
      headerName: 'Serial Number',
      flex: 1.5,
    },
    {
      field: 'machineType',
      headerName: 'Machine Type',
      sortable: false,
      flex: 1,
    },
    {
      field: 'syncProgress',
      headerName: 'Sync',
      sortable: false,
      renderCell: (p) => {
        const { id } = p.row;
        const machineSummary = syncSummary.data?.data.machineSummaries[id];
        return <SyncProgress machineSummary={machineSummary} />;
      },
      flex: 2,
    },
    {
      field: 'syncTime',
      headerName: 'Sync Time',
      sortable: false,
      renderCell: (p) => {
        const { id } = p.row;
        const machineSummary = syncSummary.data?.data.machineSummaries[id];
        return (
          <TimeElapsed
            startDate={machineSummary?.taskSummaryAggregate?.start}
            endDate={machineSummary?.taskSummaryAggregate?.end}
          />
        );
      },
      flex: 1,
    },
    {
      field: 'machineStatus',
      headerName: 'Machine Status',
      sortable: false,
      renderCell: (p) => {
        const { id } = p.row;
        const machineSummary = syncSummary.data?.data.machineSummaries[id];

        return <MachineStatusSummary machineSummary={machineSummary} />;
      },
      flex: 1,
    },
    {
      field: 'status',
      headerName: 'Connectivity',
      sortable: false,
      renderCell: (p) => <StatusIndicator status={p.value} />,
      flex: 1,
    },
    {
      field: 'action',
      headerName: 'Actions',
      sortable: false,
      flex: 1,
      renderCell: (params) => {
        const { id, serialNumber } = params.row;
        return (
          <>
            <LoadingIconButton
              onClick={() => offboardMachinesCallback([serialNumber])}
              loading={offboardMachines[1].isLoading}
              disabled={
                syncPresets[1].isLoading ||
                (syncSummaryResult?.groupTaskSummary?.aggregatedStatus !==
                  'Finished' &&
                  syncSummaryResult?.groupTaskSummary?.aggregatedStatus !==
                    undefined &&
                  syncSummaryResult?.groupTaskSummary?.aggregatedStatus !==
                    'Cancelled' &&
                  syncSummaryResult?.groupTaskSummary?.aggregatedStatus !==
                    'None' &&
                  syncSummaryResult?.groupTaskSummary?.aggregatedStatus !==
                    'Failed')
              }
            >
              <Tooltip title="Offboard machine">
                <DeleteOutlineOutlinedIcon />
              </Tooltip>
            </LoadingIconButton>

            {syncSummaryResult?.groupTaskSummary?.aggregatedStatus ===
              'InProgress' ||
            syncSummaryResult?.groupTaskSummary?.aggregatedStatus ===
              'Pending' ? (
              <LoadingIconButton
                onClick={() => requestMachineSyncCancellation(id)}
                loading={cancelMachineSync[1].isLoading}
                disabled={
                  syncSummaryResult?.machineSummaries[id]?.taskSummaryAggregate
                    ?.aggregatedStatus !== 'InProgress'
                }
              >
                <Tooltip title="Stop sync">
                  <StopCircleOutlinedIcon />
                </Tooltip>
              </LoadingIconButton>
            ) : (
              <LoadingIconButton
                onClick={() => requestSync([id])}
                loading={syncPresets[1].isLoading}
                disabled={
                  syncSummaryResult?.groupTaskSummary?.aggregatedStatus !==
                    'Finished' &&
                  syncSummaryResult?.groupTaskSummary?.aggregatedStatus !==
                    'Cancelled' &&
                  syncSummaryResult?.groupTaskSummary?.aggregatedStatus !==
                    'None' &&
                  'Finished' &&
                  syncSummaryResult?.groupTaskSummary?.aggregatedStatus !==
                    'Cancelled' &&
                  syncSummaryResult?.groupTaskSummary?.aggregatedStatus !==
                    'Failed'
                }
              >
                <Tooltip title="Sync">
                  <PlayCircleOutlinedIcon />
                </Tooltip>
              </LoadingIconButton>
            )}
          </>
        );
      },
    },
  ];

  const mapPageToCursor = useRef<Map<number, string | undefined>>(
    new Map<number, string>(),
  );
  const filter = useRef<string | undefined>(undefined);

  const [pageState, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const [rowCountState, setRowCountState] = useState({
    rowCount: 10,
    totalRowCount: -1,
    topPage: -1,
  });

  const { data, isLoading, isFetching, refetch } = useListMachinesQuery({
    machineGroupId: id,
    filter: filter.current,
    page: pageState.page,
    take: pageState.pageSize,
  });

  useEffect(() => {
    if (refetchRequired) {
      setRefetchRequired(false);
      refetch();
      syncSummary.refetch();
    }
  }, [refetchRequired, refetch, syncSummary]);

  useEffect(() => {
    if (!isLoading && !isFetching && pageState.page >= rowCountState.topPage) {
      const previousPagesCount = pageState.page * pageState.pageSize;
      const currentPageCount = data?.data.length ?? 0;
      const nextPage = data?.data.length === pageState.pageSize ? 1 : 0;
      const rowCount = previousPagesCount + currentPageCount + nextPage;
      let totalRowCount = -1;
      if (currentPageCount < pageState.pageSize) {
        totalRowCount = previousPagesCount + currentPageCount;
      }

      setRowCountState({
        rowCount,
        totalRowCount: totalRowCount ?? -1,
        topPage: pageState.page,
      });
    }
  }, [
    rowCountState.topPage,
    isLoading,
    isFetching,
    pageState.page,
    pageState.pageSize,
    data?.data.length,
  ]);

  const onPaginationModelChange = (m: GridPaginationModel) => {
    if (m.page !== pageState.page) {
      setPaginationModel({
        page: m.page,
        pageSize: pageState.pageSize,
      });
    }
  };

  const resetPaging = () => {
    mapPageToCursor.current = new Map<number, string | undefined>();
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
    setRowCountState({
      rowCount: 10,
      totalRowCount: -1,
      topPage: -1,
    });
  };

  const onSearch = (searchText: string | undefined) => {
    if (searchText !== filter.current) {
      filter.current = searchText;
      resetPaging();
    }
  };

  const [isPopupOpen, openPopup] = useState(false);

  return (
    <>
      <OnboardMachineForm
        isOpen={isPopupOpen}
        machineType={machineType}
        machineGroupId={id ?? ''}
        closeForm={(refetchRequired: boolean) => {
          openPopup(false);
          setRefetchRequired(refetchRequired);
        }}
      />
      <PageCard
        title="Machines"
        icon={<TableRowsOutlinedIcon />}
        sx={{
          '& .MuiCardHeader-root': {
            height: '80px',
          },
        }}
        contentSx={{ display: 'block' }}
        action={
          <>
            {syncSummaryResult?.groupTaskSummary?.aggregatedStatus ===
              'InProgress' ||
            syncSummaryResult?.groupTaskSummary?.aggregatedStatus ===
              'Pending' ? (
              <LoadingIconButton
                onClick={() => requestGroupSyncCancellation()}
                loading={cancelGroupSync[1].isLoading}
                disabled={isCancelGroupDisabled}
              >
                <Tooltip title="Stop All">
                  <StopCircleOutlinedIcon />
                </Tooltip>
              </LoadingIconButton>
            ) : (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeOffline}
                      onChange={(e) => setIncludeOffline(e.target.checked)}
                      disabled={
                        syncSummaryResult?.groupTaskSummary
                          ?.aggregatedStatus !== 'Finished' &&
                        syncSummaryResult?.groupTaskSummary
                          ?.aggregatedStatus !== 'Cancelled' &&
                        syncSummaryResult?.groupTaskSummary
                          ?.aggregatedStatus !== 'None' &&
                        syncSummaryResult?.groupTaskSummary
                          ?.aggregatedStatus !== 'Failed'
                      }
                    />
                  }
                  label="Include Offline"
                />
                <LoadingIconButton
                  onClick={() =>
                    requestSync(
                      selectedRows.size > 0
                        ? Array.from(selectedRows.keys()).map((key) =>
                            key.toString(),
                          )
                        : null,
                    )
                  }
                  loading={syncPresets[1].isLoading}
                  disabled={
                    syncSummaryResult?.groupTaskSummary?.aggregatedStatus !==
                      'Finished' &&
                    syncSummaryResult?.groupTaskSummary?.aggregatedStatus !==
                      'Cancelled' &&
                    syncSummaryResult?.groupTaskSummary?.aggregatedStatus !==
                      'None' &&
                    syncSummaryResult?.groupTaskSummary?.aggregatedStatus !==
                      'Failed'
                  }
                >
                  <Tooltip
                    title={selectedRows.size > 0 ? 'Sync selected' : 'Sync all'}
                  >
                    <PlayCircleOutlinedIcon />
                  </Tooltip>
                </LoadingIconButton>
              </>
            )}
            <IconButton
              onClick={() =>
                offboardMachinesCallback(
                  Array.from(selectedRows.values()).map((v) => v.serialNumber),
                )
              }
              disabled={
                syncPresets[1].isLoading ||
                (syncSummaryResult?.groupTaskSummary?.aggregatedStatus !==
                  'Finished' &&
                  syncSummaryResult?.groupTaskSummary?.aggregatedStatus !==
                    undefined &&
                  syncSummaryResult?.groupTaskSummary?.aggregatedStatus !==
                    'Cancelled' &&
                  syncSummaryResult?.groupTaskSummary?.aggregatedStatus !==
                    'None' &&
                  syncSummaryResult?.groupTaskSummary?.aggregatedStatus !==
                    'Failed') ||
                selectedRows.size <= 0
              }
            >
              <Tooltip title="Offboard selected machines">
                <DeleteOutlineOutlinedIcon />
              </Tooltip>
            </IconButton>
            <RoleBased
              requiredRole={PortalRole[PortalRole.Admin]}
              component={
                <LoadingIconButton
                  onClick={() => clearMachinePresetVersionsCallback()}
                  loading={offboardMachines[1].isLoading}
                  disabled={
                    syncPresets[1].isLoading ||
                    (syncSummaryResult?.groupTaskSummary?.aggregatedStatus !==
                      'Finished' &&
                      syncSummaryResult?.groupTaskSummary?.aggregatedStatus !==
                        undefined &&
                      syncSummaryResult?.groupTaskSummary?.aggregatedStatus !==
                        'Cancelled' &&
                      syncSummaryResult?.groupTaskSummary?.aggregatedStatus !==
                        'None' &&
                      syncSummaryResult?.groupTaskSummary?.aggregatedStatus !==
                        'Failed')
                  }
                >
                  <Tooltip
                    title={
                      selectedRows.size > 0
                        ? 'Clear selected machines preset versions'
                        : 'Clear machine preset versions'
                    }
                  >
                    <CleaningServicesOutlinedIcon />
                  </Tooltip>
                </LoadingIconButton>
              }
            />

            <IconButton
              onClick={() => openPopup(true)}
              disabled={
                syncPresets[1].isLoading ||
                (syncSummaryResult?.groupTaskSummary?.aggregatedStatus !==
                  'Finished' &&
                  syncSummaryResult?.groupTaskSummary?.aggregatedStatus !==
                    undefined &&
                  syncSummaryResult?.groupTaskSummary?.aggregatedStatus !==
                    'Cancelled' &&
                  syncSummaryResult?.groupTaskSummary?.aggregatedStatus !==
                    'None' &&
                  syncSummaryResult?.groupTaskSummary?.aggregatedStatus !==
                    'Failed')
              }
            >
              <Tooltip title="Onboard machine">
                <AddCircleOutlineOutlinedIcon />
              </Tooltip>
            </IconButton>
            <LoadingIconButton
              loading={isLoading || isFetching}
              onClick={() => setRefetchRequired(true)}
            >
              <Tooltip title="Refresh">
                <CachedOutlinedIcon fontSize="large" />
              </Tooltip>
            </LoadingIconButton>
          </>
        }
      >
        <GroupSyncSummary syncSummary={syncSummaryResult} />
        <DataGrid
          apiRef={apiRef}
          onRowSelectionModelChange={onRowSelectionModelChange}
          classes={{ row: 'selectable' }}
          columns={columns}
          rows={data?.data ?? []}
          initialState={{
            columns: {
              columnVisibilityModel: {
                id: false,
              },
            },
          }}
          loading={isFetching}
          slots={{ toolbar: CustomToolbar }}
          slotProps={{
            toolbar: {
              onSearch,
            },
            pagination: { count: rowCountState.totalRowCount },
          }}
          sx={{
            '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
              display: 'none',
            },
            '& .MuiDataGrid-overlay': {
              height: '100%',
            },
            '& .MuiTablePagination-displayedRows': {
              display: 'none',
            },
            '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus, &.MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within':
              {
                outline: 'none',
              },
          }}
          rowCount={rowCountState.rowCount}
          paginationMode="server"
          onPaginationModelChange={onPaginationModelChange}
          paginationModel={pageState}
          pageSizeOptions={[10]}
          checkboxSelection
          keepNonExistentRowsSelected
        />
      </PageCard>
    </>
  );
}

export default MachineGroupMachines;
